import VueCookies from "vue-cookies";
import CryptoJS from "crypto-js";

// Default cookie configuration
const defaultOptions = {
  expires: "1d",
  domain: process.env.NODE_ENV === "production" ? "matta.trade" : "localhost",
  path: "/",
  secure: process.env.NODE_ENV === "production",
  sameSite: "Strict",
};

// --- Encryption helpers ---
function getSecretKey() {
  const key = process.env.VUE_APP_ENCRYPTION_KEY;
  if (!key) console.error("[cookieUtils] Missing encryption key");
  return key;
}

function encrypt(value) {
  try {
    const json = JSON.stringify(value);
    return CryptoJS.AES.encrypt(json, getSecretKey()).toString();
  } catch (e) {
    console.error("[cookieUtils] Encrypt failed:", e);
    return "";
  }
}

function decrypt(cipher) {
  try {
    const bytes = CryptoJS.AES.decrypt(cipher, getSecretKey());
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch (e) {
    console.warn("[cookieUtils] Decrypt failed ? returning raw value");
    return cipher; // fallback: return original unreadable string
  }
}

export const cookieUtils = {
  /**
   * Set encrypted cookie
   */
  set(name, value, options = {}) {
    const cookieOptions = { ...defaultOptions, ...options };

    const encryptedValue = encrypt(value); // encrypt always
    VueCookies.set(
      `${name}_Dev`,
      encryptedValue,
      cookieOptions.expires,
      cookieOptions.path,
      cookieOptions.domain,
      cookieOptions.secure,
      cookieOptions.sameSite
    );
  },

  /**
   * Get decrypted cookie value
   */
  get(name) {
    const encrypted = VueCookies.get(`${name}_Dev`);
    if (!encrypted) return null;

    return decrypt(encrypted);
  },

  /**
   * Check cookie existence
   */
  has(name) {
    return VueCookies.isKey(`${name}_Dev`);
  },

  /**
   * Remove cookie
   */
  remove(name, options = {}) {
    const cookieOptions = { ...defaultOptions, ...options };
    const devName = `${name}_Dev`;
    VueCookies.remove(devName, cookieOptions.path, cookieOptions.domain);
    VueCookies.remove(name, cookieOptions.path, cookieOptions.domain);

    if (typeof document !== "undefined") {
      document.cookie = `${devName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${
        cookieOptions.path || "/"
      };`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${
        cookieOptions.path || "/"
      };`;
    }

    const store = typeof window !== "undefined" ? window.cookieStore : null;
    if (store && store.delete) {
      store
        .delete({
          name: devName,
          path: cookieOptions.path || "/",
          domain: cookieOptions.domain || undefined,
        })
        .catch?.(() => {});
      store
        .delete({
          name,
          path: cookieOptions.path || "/",
          domain: cookieOptions.domain || undefined,
        })
        .catch?.(() => {});
    }
  },

  /**
   * Remove all cookies
   */
  async removeAll(options = {}) {
    const opts = { ...defaultOptions, ...options };
    VueCookies.keys().forEach((cookieName) => {
      VueCookies.remove(cookieName, opts.path, opts.domain);
    });

    if (typeof document !== "undefined" && document.cookie) {
      const cookies = document.cookie.split(";");
      const hostname =
        typeof window !== "undefined" && window.location
          ? window.location.hostname
          : "";
      const domainParts = hostname ? hostname.split(".") : [];

      for (const cookie of cookies) {
        const eqPos = cookie.indexOf("=");
        const name = (eqPos > -1 ? cookie.slice(0, eqPos) : cookie).trim();
        if (!name) continue;

        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=;`;

        if (domainParts.length > 0 && hostname) {
          let currentDomain = "";
          for (let i = domainParts.length - 1; i >= 0; i--) {
            currentDomain = `.${domainParts[i]}${currentDomain}`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${currentDomain};`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${currentDomain.slice(
              1
            )};`;
          }
        }
      }
    }

    const store = typeof window !== "undefined" ? window.cookieStore : null;
    if (store && store.getAll && store.delete) {
      try {
        const cookies = await store.getAll();
        if (cookies && cookies.length > 0) {
          await Promise.all(
            cookies.map((cookie) =>
              store
                .delete({
                  name: cookie.name,
                  path: cookie.path || "/",
                  domain: cookie.domain || undefined,
                })
                .catch(() => {})
            )
          );
        }
      } catch (err) {
        console.warn("cookieStore.removeAll failed:", err);
      }
    }
  },

  /**
   * Get all cookie names
   */
  getAllCookieNames() {
    return VueCookies.keys();
  },
};

export default cookieUtils;
